/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, AccountLink, CRUDPermissions, Entity} from '@ion/core/src/types/entities';
import {SFTPEditorEnablement} from '@ion/dls/src/editors/SFTPConfigurationEditor';
import useAddSSOConfiguration from '@ion/query/src/hooks/useAddSSOConfiguration';
import useSSOConfiguration from '@ion/query/src/hooks/useSSOConfiguration';
import useUpdateSSOConfiguration from '@ion/query/src/hooks/useUpdateSSOConfiguration';
import {errorAddOptions, errorEditOptions, successAddOptions, successEditOptions} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {AccountEditor} from './AccountEditor';
import {auditAdminGroup, editAdminGroup, editGroup} from './state';

export interface EditGroupEditorProps extends SFTPEditorEnablement {
  refreshInitialization?: Function;
  onEdit?: Function;
  defaultAccount?: AccountLink;
  isCtcAdmin: boolean;
  isViewDialog: boolean;
  includeApplications?: boolean;
  isChipView?: boolean;
  accountsPermissions?: CRUDPermissions;
  applicationPermissions?: CRUDPermissions;
  hasAutoJobs?: boolean;
  allowSSOConfig?: boolean;
  allowDeleteSSOConfig?: boolean;
  allowUpdateSSOConfig?: boolean;
  hasCustomVisionPermission?: boolean;
  isVehicleModuleEnabled?: boolean;
}

/**
 * Display View/Edit Account Editor
 */
export const EditGroupEditor = ({
  isCtcAdmin,
  isViewDialog,
  includeApplications,
  defaultAccount,
  refreshInitialization,
  accountsPermissions,
  applicationPermissions,
  allowSSOConfig = false,
  allowDeleteSSOConfig = false,
  allowUpdateSSOConfig = false,
  includeSFTPEditor = false,
  isChipView = false,
  hasAutoJobs = false,
  hasCustomVisionPermission = false,
  isVehicleModuleEnabled = false
}: EditGroupEditorProps) => {
  const dispatch                     = useDispatch();
  const {globalGroup}                = useSelector(({globalGroup}) => globalGroup);
  const {account, roles}             = useAuthenticatedUser();
  const {
    selected: selectedGroup,
    result: editedGroup,
    loading
  }                                   = useSelector(({adminGroups: {edit}}) => edit);
  const [clearForm, setClearForm]     = useState(false);
  const isViewDialogRef               = useRef(isViewDialog);
  const isView                        = isViewDialog || false;
  const isAudit                       = useRef();
  const [isEdit, setIsEdit]           = useState();
  const isLoggedinUserAccount         = isCtcAdmin && (entityId(defaultAccount) === entityId(selectedGroup));
  const hasEnterprise                 = selectedGroup?.enterprise;

  const ssoConfigId                       = entityId(selectedGroup?.ssoConfig, false);
  const {isConfigLoading, ssoConfig}      = useSSOConfiguration(ssoConfigId, !!ssoConfigId);
  const [showSSOConfig, setShowSSOConfig] = useState(false);
  const [ssoConfigObj, setSSOConfigObj]   = useState(null);
  const [editedAccount, setEditedAccount] = useState(null);

  const {
    data,
    save,
    isSuccess: isAddSuccess,
    isError: isAddError,
    isLoading: isAddLoading
  } = useAddSSOConfiguration();
  const {
    update,
    isSuccess,
    isError,
    isLoading
  } = useUpdateSSOConfiguration();

  useEffect(() => {
    const setAudit = isAudit.current;
    if (isEdit) {
      dispatch(editAdminGroup({
        account    : selectedGroup,
        show       : true,
        isViewMode : false
      }));
    } else if (isEdit === false) {
      dispatch(auditAdminGroup(selectedGroup, true));
    }
    setIsEdit(setAudit);
  }, [isEdit]);

  useEffect(() => {
    if (editedGroup) {
      setClearForm(true);
      dispatch(editAdminGroup({
        account    : null,
        show       : false,
        isViewMode : false
      }));
    }
  }, [editedGroup]);

  useEffect(() => {
    isViewDialogRef.current = isViewDialog;
  }, [isViewDialog]);

  useEffect(() => {
    if (isSuccess) {
      const accObj = {
        ...editedAccount,
        ssoMode : ssoConfigObj?.sso_mode
      };
      dispatch(toggleUserPrompt(successEditOptions(Entity.ssoConfiguration)));
      dispatch(editGroup(accObj, true, refreshInitialization));
    }
    if (isError) {
      dispatch(toggleUserPrompt(errorEditOptions(Entity.ssoConfiguration)));
    }
  }, [isSuccess, isError, ssoConfigObj]);

  useEffect(() => {
    if (isAddSuccess && data && ssoConfigObj) {
      const accObj = {
        ...editedAccount,
        ssoMode   : ssoConfigObj?.sso_mode,
        ssoConfig : data?.self?.href
      };
      dispatch(toggleUserPrompt(successAddOptions(Entity.ssoConfiguration)));
      dispatch(editGroup(accObj, true, refreshInitialization));
    }
    if (isError) {
      dispatch(toggleUserPrompt(errorAddOptions(Entity.ssoConfiguration)));
    }
  }, [data, isAddSuccess, isAddError, ssoConfigObj]);

  const onEdit = useCallback((editValue) => {
    setIsEdit(editValue);
  }, []);

  const handleSubmit = useCallback((group: Account) => {
    if (!isViewDialogRef.current) {
      const ssoConfigData = group?.ssoInfo;
      if (ssoConfigData) {
        delete group.ssoInfo;
        delete group.sso_mode;
        const ssoObj = {account_ids: [group.id], ...ssoConfigData};
        setSSOConfigObj(ssoObj);
        setEditedAccount(group);
        if (ssoConfigId) {
          update({...ssoConfig, ...ssoObj});
        } else {
          save(ssoObj);
        }
      } else {
        dispatch(editGroup(group, isCtcAdmin, refreshInitialization));
      }
    }
  }, [isCtcAdmin, isViewDialogRef, ssoConfig]);

  const onCloseDialog = useCallback(() => {
    dispatch(editAdminGroup({
      account    : null,
      show       : false,
      isViewMode : false
    }));
  }, []);

  if (!isConfigLoading) {
    return (
      <AccountEditor open
        isChipView={isChipView}
        account={globalGroup || account}
        currentUserRoles={roles}
        defaultAccount={defaultAccount}
        group={selectedGroup}
        isViewDialog={isView}
        includeSFTPEditor={includeSFTPEditor}
        includeApplications={includeApplications}
        onSubmit={handleSubmit}
        onCloseDialog={onCloseDialog}
        onEdit={onEdit}
        clear={clearForm}
        loading={loading || isAddLoading || isLoading}
        hasAutoJobs={hasAutoJobs}
        ssoConfig={ssoConfig}
        showSSOConfig={showSSOConfig}
        setShowSSOConfig={setShowSSOConfig}
        allowSSOConfig={allowSSOConfig && hasEnterprise}
        allowDeleteSSOConfig={allowDeleteSSOConfig && hasEnterprise}
        allowUpdateSSOConfig={allowUpdateSSOConfig && hasEnterprise}
        accountsPermissions={accountsPermissions}
        applicationPermissions={applicationPermissions}
        hasCustomVisionPermission={hasCustomVisionPermission}
        isLoggedinUserAccount={isLoggedinUserAccount}
        isVehicleModuleEnabled={isVehicleModuleEnabled}
        autojobsSetting />
    );
  }
};

export default EditGroupEditor;
